import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useCart } from "../../context/CartContext";
import { products } from "../../data/products";
import { supabase } from "../../lib/supabaseClient";


const CartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6.5 6.5H21l-1.6 8.2a2 2 0 0 1-2 1.6H9.1a2 2 0 0 1-2-1.6L5.3 3.8H3"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.5 20.2a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8ZM17.2 20.2a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z"
      fill="currentColor"
    />
  </svg>
);

const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M20 21a8 8 0 0 0-16 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M12 13a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDown = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MenuIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 7h16M4 12h16M4 17h16"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SearchIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();

  // -------- CART (supports multiple shapes) --------
  const cart = useCart();
  const items =
    cart?.items || cart?.cartItems || cart?.cart || cart?.state?.items || [];

  const cartItems = Array.isArray(items) ? items : [];

  const cartCount = useMemo(() => {
    return cartItems.reduce((sum, item) => {
      const q = Number(item?.quantity ?? item?.qty ?? 0);
      return sum + (Number.isFinite(q) ? q : 0);
    }, 0);
  }, [cartItems]);

  const getQty = (it) => Number(it?.quantity ?? it?.qty ?? 1) || 1;
  const getName = (it) => it?.name ?? it?.product?.name ?? "Item";
  const getPrice = (it) => Number(it?.price ?? it?.product?.price ?? 0) || 0;

  // ✅ FIX: your cart items store `image`, not `images`
  const getImg = (it) =>
    it?.image ?? it?.images?.[0] ?? it?.product?.images?.[0] ?? null;

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, it) => sum + getPrice(it) * getQty(it), 0);
  }, [cartItems]);

  // -------- MINI CART DROPDOWN --------
  const [openCart, setOpenCart] = useState(false);
  const cartWrapRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!cartWrapRef.current) return;
      if (!cartWrapRef.current.contains(e.target)) setOpenCart(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // -------- AUTH / USER --------
  const [user, setUser] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadUser() {
      const { data } = await supabase.auth.getUser();
      if (mounted) setUser(data?.user || null);
    }

    loadUser();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (mounted) setUser(session?.user || null);
      }
    );

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, []);

  // ✅ NEW: compute a friendly display name (metadata -> email prefix -> "there")
  const displayName = useMemo(() => {
    if (!user) return "";
    const md = user.user_metadata || {};

    const fromMeta =
      md.full_name ||
      md.name ||
      md.first_name ||
      md.username;

    if (fromMeta && String(fromMeta).trim()) return String(fromMeta).trim();

    const email = user.email || "";
    if (email.includes("@")) return email.split("@")[0];

    return "there";
  }, [user]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate("/");
  }

  // -------- SEARCH --------
  const [query, setQuery] = useState("");
  const [openSearch, setOpenSearch] = useState(false);
  const searchWrapRef = useRef(null);
  const inputRef = useRef(null);

  // wipe search when leaving /shop
  useEffect(() => {
    if (!location.pathname.startsWith("/shop")) {
      setQuery("");
      setOpenSearch(false);
    }
  }, [location.pathname]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 2) return [];

    const filtered = products.filter((p) => {
      const haystack = [p.name, p.category, p.id, p.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });

    return filtered.slice(0, 6);
  }, [query]);

  useEffect(() => {
    function onDocClick(e) {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setOpenSearch(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpenSearch(false);
        setOpenCart(false);
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpenSearch(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function onSubmitSearch(e) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setOpenSearch(false);
  }

  function onPickResult(productId) {
    navigate(`/product/${productId}`);
    setOpenSearch(false);
    setQuery("");
  }

  const showDropdown = openSearch && query.trim().length >= 2;

  return (
    <header className="sticky top-0 z-50 bg-slate-50/90 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-6">
        {/* LEFT — Brand */}
<div
  onClick={() => {
    navigate("/"); // React Router navigation (no reload)
    window.scrollTo({ top: 0, behavior: "smooth" }); // optional
  }}
  className="flex items-center cursor-pointer"
>
  <img src={logo} alt="HD Labs" className="w-48 h-16 object-contain" />
</div>

        {/* CENTER — SEARCH */}
        <div className="flex-1 hidden md:block" ref={searchWrapRef}>
          <form onSubmit={onSubmitSearch} className="relative max-w-xl mx-auto">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                <SearchIcon />
              </span>

              <input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpenSearch(true);
                }}
                onFocus={() => setOpenSearch(true)}
                type="text"
                placeholder="Search products…"
                className="w-full rounded-2xl border border-slate-200 bg-white pl-12 pr-4 py-3 text-sm
                           focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {showDropdown && (
              <div className="absolute left-0 right-0 mt-2 rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                {results.length === 0 ? (
                  <div className="px-4 py-4 text-sm text-slate-600">
                    No matches for <span className="font-semibold">{query}</span>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onPickResult(p.id)}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3"
                      >
                        <div className="w-10 h-10 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                          {p.images?.[0] ? (
                            <img
                              src={p.images[0]}
                              alt={p.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-xs text-slate-500">IMG</span>
                          )}
                        </div>

                        <div className="min-w-0">
                          <div className="font-semibold text-slate-900 truncate">
                            {p.name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {p.category}
                          </div>
                        </div>

                        <div className="ml-auto text-sm font-semibold text-slate-900">
                          ${p.price}
                        </div>
                      </button>
                    ))}

                    <button
                      type="submit"
                      className="w-full text-left px-4 py-3 text-sm font-semibold text-blue-900 hover:bg-blue-50"
                    >
                      View all results for “{query}”
                    </button>
                  </div>
                )}
              </div>
            )}
          </form>
        </div>

        {/* RIGHT — NAV + ACCOUNT + CART */}
        <div className="flex items-center gap-6">
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold text-gray-600">
            {/* PRODUCTS DROPDOWN */}
            <div className="relative group">
              <Link
                to="/shop"
                onClick={() => window.scrollTo(0, 0)}
                className="
                  inline-flex items-center gap-2
                  rounded-full px-4 py-2
                  bg-blue-900 text-blue-50
                  hover:bg-blue-100 hover:text-blue-800
                  transition-colors
                "
              >
                <MenuIcon className="w-5 h-5" />
                <span>Products</span>
                <ChevronDown className="transition-transform duration-200 group-hover:rotate-180" />
              </Link>

              <div
                className="
                  absolute left-1/2 top-full -translate-x-1/2 pt-2
                  opacity-0 invisible translate-y-1
                  group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
                  transition-all duration-200 ease-out
                "
              >
                <div className="w-64 rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                  <Link
                    to="/shop?category=Peptides"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-slate-50"
                  >
                    Peptides
                  </Link>

                  <Link
                    to="/shop?category=HGH%20%26%20IGF-1"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-slate-50"
                  >
                    HGH &amp; IGF-1
                  </Link>

                  <Link
                    to="/shop?category=Sexual%20aid"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-slate-50"
                  >
                    Sexual aid
                  </Link>

                  <Link
                    to="/shop?category=Post%20%26%20On%20cycle%20Therapy"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-slate-50"
                  >
                    Post &amp; On cycle Therapy
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* ACCOUNT */}
          <div className="relative group">
            <button className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900">
              <UserIcon className="w-5 h-5" />
              {user ? `Hi, ${displayName}` : "Account"}
              <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
            </button>

            <div
              className="
                absolute left-1/2 -translate-x-1/2 top-full mt-2 w-56
                opacity-0 invisible translate-y-1
                group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
                transition-all duration-200 ease-out
                z-50
              "
            >
              <div className="rounded-2xl border border-slate-200 bg-white shadow-lg overflow-hidden">
                {user ? (
                  <>
                    <Link
                      to="/my-orders"
                      className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      My Orders
                    </Link>
                    <Link
                      to="/account"
                      className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Account Settings
                    </Link>
                    <button
                      onClick={signOut}
                      className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="block px-4 py-3 text-sm text-slate-700 hover:bg-slate-50"
                    >
                      Create Account
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* CART + MINI DROPDOWN */}
          <div className="relative" ref={cartWrapRef}>
            <button
              type="button"
              onClick={() => setOpenCart((v) => !v)}
              className="relative inline-flex items-center"
              aria-haspopup="dialog"
              aria-expanded={openCart}
            >
              <div className="w-11 h-11 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 flex items-center justify-center">
                <CartIcon />
              </div>

              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-blue-900 text-white text-xs font-bold flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            <div
              className={[
                "absolute left-1/2 -translate-x-1/2 top-full mt-2 w-[320px] rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden",
                "origin-top-right transition-all duration-150",
                openCart
                  ? "opacity-100 translate-y-0 visible"
                  : "opacity-0 -translate-y-1 invisible pointer-events-none",
              ].join(" ")}
            >
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">Cart</div>
                <button
                  type="button"
                  onClick={() => setOpenCart(false)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Close
                </button>
              </div>

              {cartItems.length === 0 ? (
                <div className="px-4 py-6 text-sm text-slate-600">
                  Your cart is empty.
                </div>
              ) : (
                <>
                  <div className="max-h-[260px] overflow-auto">
                    {cartItems.slice(0, 4).map((it, idx) => (
                      <div
                        key={it?.id ?? it?.product?.id ?? idx}
                        className="px-4 py-3 border-b border-slate-100 flex gap-3"
                      >
                        <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                          {getImg(it) ? (
                            <img
                              src={getImg(it)}
                              alt={getName(it)}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-[10px] text-slate-400">
                              IMG
                            </span>
                          )}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900 truncate">
                            {getName(it)}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            Qty: {getQty(it)}
                          </div>
                        </div>

                        <div className="text-sm font-semibold text-slate-900">
                          ${(getPrice(it) * getQty(it)).toFixed(2)}
                        </div>
                      </div>
                    ))}

                    {cartItems.length > 4 && (
                      <div className="px-4 py-2 text-xs text-slate-500">
                        + {cartItems.length - 4} more item(s)…
                      </div>
                    )}
                  </div>

                  <div className="px-4 py-3 bg-slate-50 border-t border-slate-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-600">Subtotal</span>
                      <span className="font-semibold text-slate-900">
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Link
                        to="/cart"
                        onClick={() => setOpenCart(false)}
                        className="inline-flex items-center justify-center rounded-xl border border-blue-900 text-blue-900 bg-white hover:bg-blue-50 px-3 py-2 text-sm font-semibold"
                      >
                        View cart
                      </Link>

                      <Link
                        to="/checkout"
                        onClick={() => setOpenCart(false)}
                        className="inline-flex items-center justify-center rounded-xl bg-blue-900 hover:bg-blue-800 text-white px-3 py-2 text-sm font-semibold"
                      >
                        Checkout
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
